import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Bridge, DeviceInfo } from '@casa/bridge';
import { onAppSettingsChanged, readAppSettings, type AppSettings } from '../data/app_settings.ts';
import { approachDetector } from './approach.ts';
import { brightnessFor, screenState, type ScreenState } from './screen_sleep.ts';

// The screen asleep (docs/specs/settings.md AP-2 to AP-8): dimmed or blanked by the brightness where
// the host can set it, else by a dark layer over everything. A layer is there in both cases, to catch
// the touch that wakes the screen, so that it does not reach the dashboard (AP-6).

/** How dark the layer is when the brightness cannot be lowered: dimmed, the dashboard still shows. */
const DIM_LAYER = 'rgba(0, 0, 0, 0.7)';
/** The clock is looked at this often; sleep times and dimming are to the minute. */
const TICK_MS = 1_000;
/** After the waking touch, what the browser makes of it (a click, a late pointer or mouse up) is caught too. */
const SWALLOW_MS = 500;

function layerColor(state: ScreenState, hasBrightness: boolean) : string {
    if (state === 'blanked') {
        return '#000000';
    }

    return hasBrightness ? 'transparent' : DIM_LAYER;
}

export function ScreenSleep({ bridge }: { bridge: Bridge }) : ReactNode {
    const [settings, setSettings] = useState<AppSettings>();
    const [device, setDevice] = useState<DeviceInfo>();
    const [state, setState] = useState<ScreenState>('awake');
    // Cleared when setting it fails once (the host says it can, but cannot after all).
    const [hasBrightness, setHasBrightness] = useState(false);
    const lastActivity = useRef(Date.now());
    const stateRef = useRef<ScreenState>('awake');
    stateRef.current = state;
    const swallowUntil = useRef(0);

    useEffect(() => {
        void readAppSettings(bridge).then(setSettings);
        void bridge.device.info().then(
            (info) => {
                setDevice(info);
                setHasBrightness(info.brightness === true);
            },
            (error: unknown) => console.warn(`screen sleep: the device's abilities are not known: ${String(error)}`),
        );

        return onAppSettingsChanged(setSettings);
    }, [bridge]);

    const wake = () : void => {
        lastActivity.current = Date.now();
        setState('awake');
    };

    // Coming back to the app is someone using it.
    useEffect(() => bridge.lifecycle.onForeground(wake), [bridge]);

    // The state follows the clock and the last touch.
    useEffect(() => {
        if (!settings) {
            return undefined;
        }

        const update = () : void => setState(screenState(new Date(), settings, lastActivity.current));
        update();
        const timer = setInterval(update, TICK_MS);

        return () => clearInterval(timer);
    }, [settings]);

    // A touch, a key or the wheel while awake keeps it awake; while asleep, only the layer's touch
    // wakes it, when it ends. A key while asleep only wakes it.
    useEffect(() => {
        const onActivity = () : void => {
            if (stateRef.current === 'awake') {
                lastActivity.current = Date.now();
            }
        };
        const onKey = (event: KeyboardEvent) : void => {
            if (stateRef.current !== 'awake') {
                event.preventDefault();
                event.stopPropagation();
                wake();

                return;
            }
            onActivity();
        };
        const swallow = (event: Event) : void => {
            if (Date.now() < swallowUntil.current) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
        const swallowed = ['click', 'pointerup', 'mouseup', 'touchend'] as const;
        window.addEventListener('pointerdown', onActivity, true);
        window.addEventListener('wheel', onActivity, true);
        window.addEventListener('keydown', onKey, true);
        for (const type of swallowed) {
            window.addEventListener(type, swallow, true);
        }

        return () => {
            window.removeEventListener('pointerdown', onActivity, true);
            window.removeEventListener('wheel', onActivity, true);
            window.removeEventListener('keydown', onKey, true);
            for (const type of swallowed) {
                window.removeEventListener(type, swallow, true);
            }
        };
    }, []);

    // The brightness, where the host can set it; given back to the system when awake.
    const brightnessSet = useRef(false);
    useEffect(() => {
        if (!hasBrightness) {
            return;
        }

        const level = brightnessFor(state);
        if (level === null && !brightnessSet.current) {
            return;
        }

        brightnessSet.current = level !== null;
        bridge.device.setBrightness(level).catch((error: unknown) => {
            console.warn(`screen sleep: the brightness was not set: ${String(error)}`);
            setHasBrightness(false);
        });
    }, [bridge, state, hasBrightness]);

    // Someone near, while asleep only: the sensor is not read all day (AP-2).
    const sensor = device?.sensors?.includes('proximity') ? 'proximity' : device?.sensors?.includes('light') ? 'light' : undefined;
    const watches = state !== 'awake' && settings?.wakeOnApproach === true && sensor !== undefined;
    useEffect(() => {
        if (!watches || !sensor) {
            return undefined;
        }

        let stop: (() => void) | undefined;
        let isDone = false;
        const detector = approachDetector(wake);
        bridge.device.watchSensor(sensor, (reading) => detector.reading(reading, Date.now())).then(
            (unsubscribe) => {
                if (isDone) {
                    unsubscribe();
                } else {
                    stop = unsubscribe;
                }
            },
            (error: unknown) => console.warn(`screen sleep: the ${sensor} sensor did not start: ${String(error)}`),
        );

        return () => {
            isDone = true;
            stop?.();
        };
    }, [bridge, watches, sensor]);

    if (state === 'awake') {
        return null;
    }

    // Woken when the touch ends, so that its end does not reach what lies under the layer either.
    return (
        <div
            data-testid="screen-sleep"
            data-state={state}
            aria-hidden="true"
            onPointerDown={(event) => event.preventDefault()}
            onPointerUp={() => {
                swallowUntil.current = Date.now() + SWALLOW_MS;
                wake();
            }}
            onContextMenu={(event) => event.preventDefault()}
            style={{ position: 'fixed', inset: 0, zIndex: 2147483647, background: layerColor(state, hasBrightness), cursor: 'none', touchAction: 'none' }}
        />
    );
}
